import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Fragment, useEffect } from 'react'
import Helmet from 'react-helmet'

import { usePreloadLazyComponent } from '@emico/lazy'
import { useBreakpoints } from '@emico/ui'

import CategoryFilter from './CategoryFilter'
import { useGetCategory } from './GetCategory.query'
import BlueConicContent from '../../BlueConicContent'
import CategoryCanonicals from '../../canonicals/CategoryCanonicals'
import {
    FilterModal,
    LazyCartPage,
    LazyProductPage,
    TweakwiseFilterInput,
} from '../../chunks'
import {
    Category as PrismicCategory,
    CategoryBody,
} from '../../graphql/schema.generated'
import Col from '../../layout/Col'
import Container from '../../layout/Container'
import GridItem from '../../layout/Grid/GridItem'
import PageWrapper from '../../layout/PageWrapper'
import Row from '../../layout/Row'
import NotFoundPage from '../../NotFoundPage'
import { ImageContentCard } from '../../presentation/ImageContentCard'
import { NewsletterContentCard } from '../../presentation/NewsletterContentCard'
import PageLoader from '../../presentation/PageLoader'
import { PrismicCategoryLoyaltyCard } from '../../PrismicCategoryLoyaltyCard'
import { PrismicCategoryPromoCtaBanner } from '../../PrismicCategoryPromoCtaBanner'
import PrismicCategorySeoFooter from '../../PrismicCategorySeoFooter'
import PrismicCategorySeoHeader from '../../PrismicCategorySeoHeader'
import PrismicCategoryTextOnly from '../../PrismicCategoryTextOnly'
import PrismicCategoryTopBanner from '../../PrismicCategoryTopBanner'
import { pushPercolateTag } from '../../registerPercolatePreparePage'
import Heading from '../../typography/Heading'
import usePrismicCategory from '../../usePrismicCategory'
import { CustomTweakwiseNavigationItem } from '../../useTweakwiseNavigation'
import CategoryBreadcrumbs from '../CategoryBreadcrumbs'
import { CategoryGridHeader } from '../CategoryGridHeader'
import ProductFilterPage from '../common/ProductFilterPage'
import LoadMore from '../LoadMore'
import { ProductGrid } from '../ProductGrid'

interface Props {
    id: number
}

interface GridContent {
    gridSpace: number
    row: number
    slice: React.ReactNode | undefined
    typeName: CategoryBody['__typename']
}

interface CategoryProductGridProps {
    name: string
    items: CustomTweakwiseNavigationItem[]
    total: number
    content: PrismicCategory | undefined
    loading?: boolean
}

// Validate and parse responsive grid properties
// Default mobile settings
const parseResponsiveGridProps = (gridProp?: string | null) => {
    if (!gridProp) {
        console.warn(
            'Missing grid setting for grid element. Element will not be shown.',
        )
        return
    }

    const gridProps = gridProp
        .split('/')
        .map((setting) => parseInt(setting.trim(), 10))

    // Check for missing grid props
    if (gridProps.length !== 4) {
        console.warn(
            'Incomplete grid setting for grid element. Element will not be shown.',
        )
        return
    }

    return gridProps
}

export const getResponsiveGridProps = (
    viewport: 'mobile' | 'tablet' | 'desktop',
    mobile: string | null | undefined,
    tablet: string | null | undefined,
    desktop: string | null | undefined,
) => {
    let gridArea

    switch (viewport) {
        case 'desktop':
            gridArea = parseResponsiveGridProps(desktop)
            break
        case 'tablet':
            gridArea = parseResponsiveGridProps(tablet)
            break
        default:
            gridArea = parseResponsiveGridProps(mobile)
            break
    }

    if (!gridArea) {
        return
    }

    // Validate props.
    // We do this because with the incorrect values,
    // a layout can break.
    let colstart: number
    let colspan: number
    let rowstart: number
    let rowspan: number

        // eslint-disable-next-line prefer-const
    ;[rowstart, colstart, rowspan, colspan] = gridArea

    const maxRows = 2
    const maxCols = viewport === 'desktop' ? 4 : viewport === 'tablet' ? 3 : 2

    // Limit input values to viewport maximums.
    // F.e. if the user has entered 1/2/1/2 on mobile,
    // it means that the colstart = 2 and the colspan = 2.
    // By default, the grid on mobile is 2 columns, but
    // because we use auto placement, and the user has endered an "incorrect" grid value,
    // the grid grows to 3 columns (start at 2, 2 columns wide), breaking the layout.
    //
    // So we check the values of colstart and colspan,
    // and make sure they don't exceed the limit for the active viewport
    colspan =
        colstart - 1 + colspan > maxCols ? maxCols - colstart + 1 : colspan
    rowspan = rowspan > maxRows ? maxRows : rowspan

    return {
        gridArea: `${rowstart} / ${colstart}/ span ${rowspan}/ span ${colspan}`,
        gridSpace: rowspan * colspan,
        row: rowstart,
        columnSpan: colspan,
    }
}

const renderSlice = (
    slice: CategoryBody,
    currentIndex: number,
    viewport: 'mobile' | 'tablet' | 'desktop',
): GridContent | undefined => {
    const isDesktop = viewport === 'desktop'

    switch (slice.__typename) {
        case 'CategoryBodyEndOfContent': {
            const {
                header,
                image,
                mobileImage,
                buttonText,
                buttonUrl,
                mobile,
                tablet,
                desktop,
            } = slice?.primary || {}

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )

            if (isDesktop && !image?.url) {
                return
            }
            if (!isDesktop && !mobileImage?.url) {
                return
            }
            if (!gridProps) {
                return
            }

            const { gridSpace, row, columnSpan } = gridProps
            const imageUrl = isDesktop ? image?.url : mobileImage?.url
            const altText = isDesktop ? image?.alt : mobileImage?.alt

            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="end"
                        key="endofcontent"
                        style={{
                            gridColumn: `span ${columnSpan}`,
                        }}
                    >
                        <ImageContentCard
                            header={header ?? undefined}
                            imageUrl={imageUrl}
                            imageMobileUrl={imageUrl}
                            altText={altText ?? undefined}
                            buttonText={buttonText ?? undefined}
                            buttonLink={buttonUrl ?? undefined}
                        />
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }
        case 'CategoryBodyImageOnlyBlock': {
            const {
                image,
                mobile,
                tablet,
                desktop,
                header,
                subheader,
                buttonText,
                buttonUrl,
                mobileImage,
                videoId,
                mobileVideoId,
            } = slice?.primary || {}

            if (isDesktop && !image?.url) {
                return
            }
            if (!isDesktop && !mobileImage?.url) {
                return
            }

            const imageUrl = isDesktop ? image?.url : mobileImage?.url
            const altText = isDesktop ? image?.alt : mobileImage?.alt
            const video = isDesktop ? videoId : mobileVideoId

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )

            if (!gridProps) {
                return
            }
            const { gridSpace, row, gridArea, columnSpan } = gridProps

            // An image is fluid when it is not displayed full width
            // Images displayed full width shouldnt be fluid
            // because then they wouldnt have a height.
            // Mobile images are never fluid
            let isFluid = false

            switch (viewport) {
                case 'desktop':
                    isFluid = columnSpan !== 4
                    break
                case 'tablet':
                    isFluid = columnSpan !== 3
                    break
            }

            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="image"
                        style={{
                            gridArea,
                        }}
                        key={`content-${currentIndex}`}
                    >
                        <BlueConicContent
                            blueConicPosition={`flavorblock${currentIndex}`}
                        >
                            <ImageContentCard
                                imageUrl={imageUrl}
                                imageMobileUrl={imageUrl}
                                header={header ?? undefined}
                                subheader={subheader ?? undefined}
                                buttonText={buttonText ?? undefined}
                                buttonLink={buttonUrl ?? undefined}
                                videoId={video ?? undefined}
                                altText={altText ?? undefined}
                                fluid={isFluid}
                                isPromotion
                            />
                        </BlueConicContent>
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }
        case 'CategoryBodyTextOnly': {
            const { header, subheading, content, mobile, tablet, desktop } =
                slice?.primary || {}

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )

            if (!gridProps) {
                return
            }
            const { gridSpace, row, gridArea, columnSpan } = gridProps

            // An image is fluid when it is not displayed full width
            // Images displayed full width shouldnt be fluid
            // because then they wouldnt have a height.
            // Mobile images are never fluid
            let isFluid = false

            switch (viewport) {
                case 'desktop':
                    isFluid = columnSpan !== 4
                    break
                case 'tablet':
                    isFluid = columnSpan !== 3
                    break
            }

            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="text"
                        style={{
                            gridArea,
                        }}
                        key={`content-${currentIndex}`}
                    >
                        <PrismicCategoryTextOnly
                            heading={header}
                            subheading={subheading ?? undefined}
                            content={content}
                            fluid={isFluid}
                        />
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }
        case 'CategoryBodyNewsletter': {
            const {
                header,
                subheader,
                image,
                mobileImage,
                mobile,
                tablet,
                desktop,
                source,
                thankYouHeader,
                thankYouSubheader,
            } = slice?.primary || {}

            if (!image?.url) {
                return
            }

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )

            if (!gridProps) {
                return
            }
            const { gridSpace, row, gridArea, columnSpan } = gridProps

            // An image is fluid when it is not displayed full width
            // Images displayed full width shouldnt be fluid
            // because then they wouldnt have a height.
            // Mobile images are never fluid
            let isFluid = false

            switch (viewport) {
                case 'desktop':
                    isFluid = columnSpan !== 4
                    break
                case 'tablet':
                    isFluid = columnSpan !== 3
                    break
            }

            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="newsletter"
                        style={{
                            gridArea,
                        }}
                        key={`content-${currentIndex}`}
                    >
                        <BlueConicContent
                            blueConicPosition={
                                `flavorblock${
                                    currentIndex + 1
                                }` as 'flavorblock'
                            }
                        >
                            <NewsletterContentCard
                                source={source || undefined}
                                imageUrl={image.url}
                                mobileImageUrl={mobileImage?.url}
                                header={header || undefined}
                                subheader={subheader || undefined}
                                fluid={isFluid}
                                thankYouHeader={thankYouHeader || undefined}
                                thankYouSubheader={
                                    thankYouSubheader || undefined
                                }
                            />
                        </BlueConicContent>
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }
        case 'CategoryBodyPromoCtaBanner': {
            if (!slice?.primary) {
                return
            }
            const { mobile, desktop, tablet } = slice?.primary || {}

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )

            if (!gridProps) {
                return
            }
            const { gridSpace, row, gridArea } = gridProps

            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="text"
                        style={{
                            gridArea,
                        }}
                        key={`content-${currentIndex}`}
                    >
                        <PrismicCategoryPromoCtaBanner {...slice.primary} />
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }

        case 'CategoryBodyLoyaltyCard': {
            if (!slice?.primary) {
                return
            }
            const { mobile, desktop, tablet } = slice?.primary || {}

            const gridProps = getResponsiveGridProps(
                viewport,
                mobile,
                tablet,
                desktop,
            )
            if (!gridProps) {
                return
            }
            const { gridSpace, row, gridArea } = gridProps
            return {
                gridSpace,
                row,
                slice: (
                    <GridItem
                        data-content="text"
                        style={{
                            gridArea,
                        }}
                        key={`content-${currentIndex}`}
                    >
                        <PrismicCategoryLoyaltyCard {...slice.primary} />
                    </GridItem>
                ),
                typeName: slice.__typename,
            }
        }
        default:
            console.warn(
                `Category slice type "${slice.__typename}" not implemented`,
            )
            return undefined
    }
}

const mapPrismicCategorySlices = (
    itemCount: number,
    viewport: 'mobile' | 'tablet' | 'desktop',
    prismicCategory?: PrismicCategory,
): { itemCount: number; slices: React.ReactNode[] } => {
    let count = itemCount
    const slices =
        prismicCategory?.body && prismicCategory.body.length > 0
            ? prismicCategory.body.reduce<GridContent[]>(
                  (acc, slice, currentIndex) => {
                      const content = renderSlice(slice, currentIndex, viewport)

                      if (content?.slice) {
                          acc.push(content)
                      }
                      return acc
                  },
                  [],
              )
            : []

    // Determine if item should render
    // based on position in the grid and the amount
    // of products and content shown.
    const positionedSlices = [...slices]
        .sort((a, b) => a.row - b.row)
        .map((sliceData) => {
            let colCount

            switch (viewport) {
                case 'desktop':
                    colCount = 4
                    break
                case 'tablet':
                    colCount = 3
                    break
                default:
                    colCount = 2
                    break
            }
            const { gridSpace, row, slice, typeName } = sliceData

            // End-of-grid content should always be shown
            if (typeName === 'CategoryBodyEndOfContent') {
                return slice
            }

            // Determine how many cells are shown before the slice
            const itemsBefore = row * colCount

            // Determine if there are still products to be displayed and if the slice should be rendered
            if (count >= itemsBefore) {
                count += gridSpace
                return slice
            }
            return null
        })

    return {
        itemCount: count,
        slices: positionedSlices,
    }
}

const CategoryProductGrid = ({
    name,
    items,
    content,
}: CategoryProductGridProps) => {
    const { isDesktop, isTablet } = useBreakpoints()
    const viewport = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'

    // Determine which blocks should be shown based on the item count and the blocks already shown
    const { slices: gridContent } = mapPrismicCategorySlices(
        items.length,
        viewport,
        content,
    )

    const gridItems = [...gridContent]

    return (
        <ProductGrid listName={name} content={gridItems}>
            {items}
        </ProductGrid>
    )
}

const CategoryHeading = styled(Heading)`
    margin: 25px 0 20px;
    text-align: center;
`

const CategoryPage = ({ id }: Props) => {
    const { isMobile } = useBreakpoints()

    usePreloadLazyComponent(isMobile ? FilterModal : TweakwiseFilterInput)
    usePreloadLazyComponent(LazyProductPage)
    usePreloadLazyComponent(LazyCartPage)

    useEffect(() => {
        pushPercolateTag(`cat_c_${id}`)
    }, [id])

    // Do not use the id from the params anywhere else on this page.
    // Apollo shows stale data while loading a new category, and
    // so use Apollo's (stale) id so that the page is consistent.
    const { data: category, error } = useGetCategory(id)

    const { data: prismicCategory } = usePrismicCategory(id)

    if (error) {
        throw error
    }

    if (!category) {
        return (
            <PageWrapper pageType="category">
                <PageLoader fullScreen reason="Loading category data..." />
            </PageWrapper>
        )
    }

    if (!category.id || !category.name) {
        return <NotFoundPage />
    }

    return (
        <PageWrapper pageType="category">
            {/* This Fragment is required to fix a "Maximum call
                stack size exceeded" crash in the react-helmet
                library. It may be related to the meta_title being
                null, but the fix fixes it even if it is null. */}
            <Fragment key={category.id}>
                <Helmet>
                    <title>{category.metaTitle}</title>
                    <meta
                        name="description"
                        content={category.metaDescription ?? ''}
                    />
                    <meta
                        name="keywords"
                        content={category.metaKeywords ?? ''}
                    />
                    {category.metaRobots && (
                        <meta name="robots" content={category.metaRobots} />
                    )}
                    {process.env.REACT_APP_HIGHSTREET_STORE_NAME && (
                        <meta
                            property="al:ios:url"
                            content={`${process.env.REACT_APP_HIGHSTREET_STORE_NAME}://categories/${category.id}`}
                        />
                    )}
                    <meta property="bc:producttype" content={category.name} />
                </Helmet>
            </Fragment>

            <CategoryCanonicals id={id} />

            <ProductFilterPage
                categoryId={category.id}
                pageName={category.name}
            >
                {({
                    mobileFilters,
                    desktopFilters,
                    items,
                    numResults: numTotalResults,
                    loadNextPage,
                    loadingNextPage,
                    loading,
                }) => (
                    <>
                        <CategoryGridHeader
                            breadcrumbs={
                                <CategoryBreadcrumbs category={category} />
                            }
                        />

                        <Container data-testid="category">
                            <PrismicCategoryTopBanner
                                content={prismicCategory}
                            />
                            <CategoryHeading variant="h1" element="h1">
                                {category.name}
                            </CategoryHeading>
                            <PrismicCategorySeoHeader
                                content={prismicCategory}
                            />
                            <CategoryFilter id={id} />
                        </Container>

                        {mobileFilters}

                        {desktopFilters}

                        <Container>
                            <Row
                                css={css`
                                    opacity: ${loading ? 0.5 : 1};
                                `}
                            >
                                <Col>
                                    <CategoryProductGrid
                                        name={category.name ?? ''}
                                        items={items}
                                        total={numTotalResults}
                                        content={prismicCategory}
                                    />

                                    {loadNextPage && (
                                        <LoadMore
                                            numItemsSeen={items.length}
                                            numTotalResults={numTotalResults}
                                            onClick={loadNextPage}
                                            loading={loadingNextPage}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </Container>
                    </>
                )}
            </ProductFilterPage>
            <PrismicCategorySeoFooter content={prismicCategory} />
        </PageWrapper>
    )
}

export default CategoryPage
